import type { z } from 'zod';
import { POSITION_REQUIRED, type VoyageReportKind, voyageReportDataSchemas } from './schemas.js';

/**
 * Report Templates (ADR 0082 Ek §5; sahip kararı 2026-10-19): rapor türlerinin alanlarını gösteren
 * salt okunur katalog. Ayrı şablon kaydı yoktur; alanlar, sıraları, türleri ve zorunlulukları
 * güncel şemadan (sürüm 2) türetilir. Gönderimde ayrıca istenen alanlar (günlük raporlar; ADR 0059
 * §3) ve seçeneklerin kuralları formda ve sunucudadır.
 */

export type TemplateFieldType =
  | 'text'
  | 'number'
  | 'time'
  | 'option'
  | 'flag'
  | 'block'
  | 'list'
  | 'position';

export interface TemplateField {
  key: string;
  type: TemplateFieldType;
  required: boolean;
}

/**
 * Arayüzde etiketi olan alan adları. Şemaya yeni alan eklenince buraya ve çevirilere eklenir
 * (`report-templates.test.ts` denetler).
 */
export const REPORT_TEMPLATE_FIELD_KEYS = [
  'reportedAt',
  'tzOffsetMin',
  'position',
  'port',
  'portCode',
  'terminal',
  'nextPort',
  'nextPortCode',
  'nextPortEta',
  'distanceToNextPortNm',
  'nextPortUnknownReason',
  'pilotOnBoard',
  'pilotOff',
  'pilotNotUsed',
  'tugsUsed',
  'tugs',
  'draft',
  'loadCondition',
  'airDraftM',
  'displacementMt',
  'cargo',
  'classification',
  'voyageAction',
  'noVoyageReason',
  'checklist',
  'openDefects',
  'portCall',
  'rob',
  'lubes',
  'freshWater',
  'weather',
  'persons',
  'shipCondition',
  'remarks',
  'nextOperation',
  'course',
  'rpm',
  'speedKn',
  'navigation',
  'machinery',
  'fuel',
  'distanceSinceLast',
  'distanceToPortNm',
  'nor',
  'firstLine',
  'allFast',
  'berthSide',
  'distanceToShoreM',
  'mooring',
  'reason',
  'location',
  'etd',
  'etdRemarks',
  'cargoProgress',
  'runningHours',
  'consumptionMt',
  'name',
  'operation',
  'charterPartyNo',
  // Tüketim bölümü (ADR 0117 §2.1)
  'consumption',
] as const;

interface Def {
  type: string;
  innerType?: z.ZodType;
  format?: string;
}
const def = (schema: z.ZodType): Def => (schema as unknown as { _zod: { def: Def } })._zod.def;

function fieldType(schema: z.ZodType): TemplateFieldType {
  let inner = schema;
  while (['optional', 'nullable'].includes(def(inner).type)) {
    inner = def(inner).innerType as z.ZodType;
  }
  const { type, format } = def(inner);
  switch (type) {
    case 'string':
      return format === 'datetime' ? 'time' : 'text';
    case 'number':
      return 'number';
    case 'enum':
      return 'option';
    case 'boolean':
      return 'flag';
    case 'array':
      return 'list';
    default:
      return 'block';
  }
}

/**
 * Türün alanları: önce bütün raporların ortak alanları (olay zamanı UTC, saat farkı, konum), sonra
 * türün verisi şema sırasıyla. Zorunlu = alan boş bırakılamaz (şemada isteğe bağlı değil).
 */
export function reportTemplateFields(kind: VoyageReportKind): TemplateField[] {
  const schema = voyageReportDataSchemas[kind] as unknown as {
    shape: Record<string, z.ZodType>;
  };
  return [
    { key: 'reportedAt', type: 'time', required: true },
    { key: 'tzOffsetMin', type: 'number', required: true },
    { key: 'position', type: 'position', required: POSITION_REQUIRED.has(kind) },
    ...Object.entries(schema.shape).map(([key, field]) => ({
      key,
      type: fieldType(field),
      required: !field.safeParse(undefined).success,
    })),
  ];
}
